<template>
  <div
      class="d-flex flex-column justify-content-center align-items-center border rounded-3 p-2 session-time"
      :class="{
        'border-secondary': sessionAvailable,
        'border-disabled': !sessionAvailable,
        'border-1': !selected,
        'border-2': selected,
      }"
      :style="{ borderColor: selected ? timeBG + '!important' : '' }"
      :role="sessionAvailable ? 'button' : undefined"
      @click="sessionAvailable ? emit('sessionChosen', session) : undefined"
      v-bind="availabilityData.tooltipAttrs"
  >
    <p
        class="mb-0 fw-bold"
        :class="{
        'text-decoration-line-through': !sessionAvailable || props.session.cutoffStatus === 'display_custom_cutoff_message',
        'text-disabled': !sessionAvailable || props.session.cutoffStatus === 'display_custom_cutoff_message'
      }"
    >
      {{ moment(sessionStartTime(session)).format('HH:mm') }}
    </p>

    <span
        v-if="sessionViewMode == 'saleCutoffDisplay' || sessionViewMode == 'standardView'"
        :class="availabilityData.class"
    >
      <small><i :class="availabilityData.icon"></i>{{ availabilityData.text }}</small>
    </span>

    <span
        v-if="sessionViewMode == 'sessionAvailabilityDetail'"
    >
      <small :class="sessionAvailabilityDetails.class" class="badge rounded-pill session-availability-details">{{ sessionAvailabilityDetails.text }}</small>
    </span>
  </div>
</template>

<script setup>
import {sessionStartTime} from "../functions/sessionFunctions";
import moment from "moment/moment";
import {computed} from "vue";
import { onMounted } from 'vue';

onMounted(() => {
  const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.forEach(el => {
    new bootstrap.Tooltip(el);
  });
});

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  selected: {
    type: Boolean,
    required: true,
  },
  timeBG: {
    type: String,
    required: true,
  },
  lowCapacityThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  saleCutoffOptions: {
    type: Object,
    required: false,
    default: () => ({
      behaviour: 'do_not_show',
      message: null,
      phone: null,
    }),
  },
  customSoldOutThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  showSessionAvailability: {
    type: Boolean,
    default: false
  },
  // Effective spaces (lower of session capacity and sales limit), falls back to session.spacesAvailable
  spacesAvailable: {
    type: Number,
    default: null,
    required: false,
  }
});

const emit = defineEmits(['sessionChosen']);

const sessionViewMode = computed(() => {
  if (props.session.cutoffStatus == 'direct_to_phone' || props.session.cutoffStatus == 'display_custom_cutoff_message') {
    return 'saleCutoffDisplay'
  }
  if (props.showSessionAvailability) {
    return 'sessionAvailabilityDetail'
  }
  return 'standardView'
});

const effectiveSpaces = computed(() => props.spacesAvailable ?? props.session.spacesAvailable);

const sessionAvailable = computed(() =>
    effectiveSpaces.value > props.customSoldOutThreshold && props.session.cutoffStatus !== 'display_custom_cutoff_message'
);

// Change Session view depending on Sales Cut-off behaviour
const availabilityData = computed(() => {
  const cutoffStatus = props.session.cutoffStatus;
  if (effectiveSpaces.value <= props.customSoldOutThreshold) {
    return {
      text: 'Sold Out',
      class: 'text-danger mobile-hide-icon',
      icon: ['fa-sharp', 'fa-solid', 'fa-circle', 'me-2'],
      tooltipAttrs: {},
    };
  }
  if (cutoffStatus === 'direct_to_phone') {
    return {
      text: null,
      class: null,
      icon: ['fa-solid fa-circle-phone', 'fs-6'],
      tooltipAttrs: {
        'data-bs-toggle': 'tooltip',
        'data-bs-placement': 'top',
        'data-bs-custom-class': 'widget-tooltip',
        title: 'Online sales closed'
      }
    };
  }
  if (cutoffStatus === 'display_custom_cutoff_message') {
    return {
      text: 'Unavailable',
      class: 'text-secondary',
      icon: ['fa-regular', 'fa-circle-info', 'me-2', 'fs-6'],
      tooltipAttrs: {
        'data-bs-toggle': 'tooltip',
        'data-bs-placement': 'top',
        'data-bs-custom-class': 'widget-tooltip',
        title: props.saleCutoffOptions.message || 'Online sales closed'
      }
    };
  }
  if (effectiveSpaces.value <= props.lowCapacityThreshold) {
    return {
      text: 'Limited',
      class: 'text-warning mobile-hide-icon',
      icon: ['fa-sharp', 'fa-solid', 'fa-circle', 'me-2'],
      tooltipAttrs: {},
    };
  }
  return {
    text: 'Available',
    class: 'text-success mobile-hide-icon',
    icon: ['fa-sharp', 'fa-solid', 'fa-circle', 'me-2'],
    tooltipAttrs: {},
  };
});

// Change view if 'Show Session Availability' enabled
const sessionAvailabilityDetails =  computed(() => {
  const sessionAvailability = effectiveSpaces.value;
  if (sessionAvailability <= props.customSoldOutThreshold) {
    return {
      text: 'Sold Out',
      class: 'bg-danger'
    };
  }
  if (sessionAvailability === 1) {
    return {
      text: `${sessionAvailability} space`,
      class: 'bg-warning text-dark'
    }
  }
  if (sessionAvailability <= props.lowCapacityThreshold) {
    return {
      text: `${sessionAvailability} spaces`,
      class: 'bg-warning text-dark'
    }
  }
  return {
    text: `${sessionAvailability} spaces`,
    class: 'bg-success'
  }
});

</script>

<style scoped lang="scss">
.session-time {
  // Force a fixed height to ensure box-sizing works. Width is controlled by the col-4 class
  height: 68px;
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;

  i {
    font-size: 12px
  }
  border-radius: 6px;
  background: #fff;
}

.text-disabled {
  text-decoration: underline;
  text-underline-offset: -50%;
  text-decoration-skip-ink: none;
}

@media (max-width: 415px) {
  .mobile-hide-icon i { display: none; }
}

.session-availability-details { font-size: 12px; padding: 4px 16px 4px 16px; }

</style>